import { articles } from "../data/articles.js";
import { courses } from "../data/courses.js";
import { downloads } from "../data/downloads.js";
import { episodes } from "../data/episodes.js";
import { events } from "../data/events.js";
import { library } from "../data/library.js";
import { getAllMediaSeasons, getMediaCycleForSeason, getPublicMediaSeasons, mediaCycles } from "../data/mediaCycles.js";
import { programs } from "../data/programs.js";
import { seasons } from "../data/seasons.js";
import { pageContent } from "../data/siteContent.js";
import { DEFAULT_LOCALE, localeCodes } from "../i18n/locales.js";
import { getArticleDescription, getArticleImageAlt, getArticleSection, getArticleTags, getArticleTitle } from "../lib/articlePresentation.js";
import { TODO_TRANSLATE } from "../lib/i18nField.js";
import { normalizeDisplayText } from "../lib/textNormalization.js";
import { resolveRoute } from "../lib/resolveRoute.js";
import { defaultDescriptions, sectionLabels } from "./metaDefaults.js";

const localizedSectionLabels = {
  home: { en: "Home", es: "Inicio", nl: "Home", pap: "Inisio" },
  "start-here": { en: "Start Here", es: "Comienza Aquí", nl: "Begin Hier", pap: "Kuminsa Aki" },
  programs: { en: "Podcasts", es: "Podcasts", nl: "Podcasts", pap: "Podcasts" },
  media: { en: "Videos", es: "Videos", nl: "Video's", pap: "Videos" },
  seasons: { en: "Seasons", es: "Temporadas", nl: "Seizoenen", pap: "Temporadanan" },
  episodes: { en: "Episodes", es: "Episodios", nl: "Afleveringen", pap: "Episodionan" },
  courses: { en: "Courses", es: "Cursos", nl: "Cursussen", pap: "Kursonan" },
  events: { en: "Events", es: "Eventos", nl: "Evenementen", pap: "Eventonan" },
  articles: { en: "Articles", es: "Artículos", nl: "Artikelen", pap: "Artikulonan" },
  library: { en: "Library", es: "Biblioteca", nl: "Bibliotheek", pap: "Biblioteka" },
  downloads: { en: "Downloads", es: "Descargas", nl: "Downloads", pap: "Downloadnan" },
  about: { en: "About", es: "Acerca de", nl: "Over", pap: "Tokante" },
  donate: { en: "Donate", es: "Donar", nl: "Doneer", pap: "Doná" }
};

const localizedPageTitles = {
  home: { en: "Enter the Work", es: "Entra en la Obra", nl: "Betreed het Werk", pap: "Drenta den e Obra" }
};

function sectionLabelFor(sectionKey, lang) {
  return localizedSectionLabels[sectionKey]?.[lang] || sectionLabels[sectionKey] || sectionKey;
}

function text(field, lang = DEFAULT_LOCALE) {
  if (typeof field === "string") return normalizeDisplayText(field, { stripMarkdown: true });
  if (!field || typeof field !== "object") return "";
  const localized = field[lang];
  if (localized && localized !== TODO_TRANSLATE) return normalizeDisplayText(localized, { stripMarkdown: true });
  const fallback = field[DEFAULT_LOCALE];
  if (fallback && fallback !== TODO_TRANSLATE) return normalizeDisplayText(fallback, { stripMarkdown: true });
  return "";
}

function truncate(value, max = 260) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1).trim()}…`;
}

function cleanDescription(value, fallbackKey) {
  const fallback = defaultDescriptions[fallbackKey] || defaultDescriptions.home;
  return truncate(value || fallback);
}

function localizedPaths(getPath) {
  return Object.fromEntries(localeCodes.map((lang) => [lang, getPath(lang)]));
}

function baseRecord({
  id,
  lang,
  path,
  sectionKey,
  title,
  description,
  entity = null,
  parentIds = [],
  image = null,
  imageAlt = null,
  author = null,
  datePublished = null,
  dateModified = null,
  articleSection = null,
  tags = []
}) {
  return {
    id,
    lang,
    path,
    sectionKey,
    sectionLabel: sectionLabelFor(sectionKey, lang),
    title: normalizeDisplayText(title || sectionLabelFor(sectionKey, lang) || "The Great Work Experience", { stripMarkdown: true }),
    description: cleanDescription(description, sectionKey),
    entity,
    parentIds,
    image,
    imageAlt,
    author,
    datePublished,
    dateModified,
    articleSection,
    tags
  };
}

function staticRecord(sectionKey, lang, title, description) {
  return baseRecord({
    id: `${sectionKey}:${lang}`,
    lang,
    path: resolveRoute(lang, sectionKey),
    sectionKey,
    title,
    description
  });
}

export function getRouteRecords() {
  const records = [];

  for (const lang of localeCodes) {
    records.push(
      baseRecord({
        id: `home:${lang}`,
        lang,
        path: resolveRoute(lang, "home"),
        sectionKey: "home",
        title: localizedPageTitles.home[lang] || localizedPageTitles.home.en,
        description: text(pageContent.home.heroText, lang)
      })
    );
    records.push(staticRecord("start-here", lang, sectionLabelFor("start-here", lang), text(pageContent.startHere.intro, lang)));
    records.push(staticRecord("programs", lang, sectionLabelFor("programs", lang), text(pageContent.indexes.programs.intro, lang)));
    records.push(staticRecord("media", lang, sectionLabelFor("media", lang), text(pageContent.indexes.media.intro, lang)));
    records.push(staticRecord("courses", lang, sectionLabelFor("courses", lang), text(pageContent.indexes.courses.intro, lang)));
    records.push(staticRecord("events", lang, sectionLabelFor("events", lang), text(pageContent.indexes.events.intro, lang)));
    records.push(staticRecord("articles", lang, sectionLabelFor("articles", lang), text(pageContent.indexes.articles.intro, lang)));
    records.push(staticRecord("library", lang, sectionLabelFor("library", lang), text(pageContent.indexes.library.intro, lang)));
    records.push(staticRecord("downloads", lang, sectionLabelFor("downloads", lang), text(pageContent.indexes.downloads.intro, lang)));
    records.push(staticRecord("about", lang, sectionLabelFor("about", lang), text(pageContent.about.intro, lang)));
    records.push(staticRecord("donate", lang, sectionLabelFor("donate", lang), text(pageContent.donate.intro, lang)));

    for (const mediaSeason of getPublicMediaSeasons()) {
      const cycle = getMediaCycleForSeason(mediaSeason.id);
      records.push(
        baseRecord({
          id: `media-season:${mediaSeason.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "media", { slug: mediaSeason.slug[lang] }),
          sectionKey: "media",
          title: text(mediaSeason.title, lang),
          description: `${cycle ? text(cycle.title, lang) : "Videos"} ${text(mediaSeason.subtitle, lang)} ${text(mediaSeason.description, lang)}`,
          entity: { type: "mediaSeason", id: mediaSeason.id },
          parentIds: cycle ? [cycle.id] : []
        })
      );
    }

    for (const program of programs) {
      records.push(
        baseRecord({
          id: `program:${program.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "programs", { slug: program.slug[lang] }),
          sectionKey: "programs",
          title: text(program.title, lang),
          description: `${text(program.subtitle, lang)} ${text(program.transformationPurpose, lang)}`,
          entity: { type: "program", id: program.id }
        })
      );
    }

    for (const season of seasons) {
      const program = programs.find((item) => item.id === season.programId);
      if (!program) continue;
      records.push(
        baseRecord({
          id: `season:${season.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "programs", { programSlug: program.slug[lang], seasonSlug: season.slug[lang] }),
          sectionKey: "seasons",
          title: text(season.title, lang),
          description: `${text(season.question, lang)} ${text(season.transformationPurpose, lang)}`,
          entity: { type: "season", id: season.id },
          parentIds: [program.id]
        })
      );
    }

    for (const episode of episodes) {
      records.push(
        baseRecord({
          id: `episode:${episode.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "episodes", { slug: episode.slug[lang] }),
          sectionKey: "episodes",
          title: text(episode.title, lang),
          description: text(episode.summary, lang),
          entity: { type: "episode", id: episode.id },
          parentIds: [episode.seasonId]
        })
      );
    }

    for (const course of courses) {
      records.push(
        baseRecord({
          id: `course:${course.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "courses", { slug: course.slug[lang] }),
          sectionKey: "courses",
          title: text(course.title, lang),
          description: `${text(course.description, lang)} ${text(course.transformationPromise, lang)}`,
          entity: { type: "course", id: course.id }
        })
      );
    }

    for (const article of articles) {
      records.push(
        baseRecord({
          id: `article:${article.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "articles", { slug: article.slug[lang] }),
          sectionKey: "articles",
          title: getArticleTitle(article, lang),
          description: getArticleDescription(article, lang),
          entity: { type: "article", id: article.id },
          image: article.image,
          imageAlt: getArticleImageAlt(article, lang),
          author: article.author,
          datePublished: article.date,
          dateModified: article.date,
          articleSection: getArticleSection(article),
          tags: getArticleTags(article)
        })
      );
    }

    for (const download of downloads) {
      records.push(
        baseRecord({
          id: `download:${download.id}:${lang}`,
          lang,
          path: resolveRoute(lang, "downloads", { slug: download.slug[lang] }),
          sectionKey: "downloads",
          title: text(download.title, lang),
          description: `${text(download.shortDescription, lang)} ${text(download.useContext, lang)}`,
          entity: { type: "download", id: download.id }
        })
      );
    }
  }

  return records.sort((a, b) => a.path.localeCompare(b.path));
}

export function getRouteRecord(pathname) {
  const normalized = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  return getRouteRecords().find((record) => {
    const recordPath = record.path.endsWith("/") && record.path !== "/" ? record.path.slice(0, -1) : record.path;
    return recordPath === normalized;
  });
}

export function getEntityAlternatePaths(record) {
  if (!record) return localizedPaths((lang) => resolveRoute(lang, "home"));
  const { entity } = record;

  if (!entity) {
    return localizedPaths((lang) => {
      if (record.sectionKey === "home") return resolveRoute(lang, "home");
      return resolveRoute(lang, record.sectionKey);
    });
  }

  const collections = {
    program: programs,
    season: seasons,
    mediaSeason: getAllMediaSeasons(),
    episode: episodes,
    course: courses,
    article: articles,
    download: downloads
  };
  const item = collections[entity.type]?.find((entry) => entry.id === entity.id);

  if (!item) return localizedPaths((lang) => resolveRoute(lang, "home"));

  if (entity.type === "season") {
    const program = programs.find((entry) => entry.id === item.programId);
    return localizedPaths((lang) => resolveRoute(lang, "programs", { programSlug: program.slug[lang], seasonSlug: item.slug[lang] }));
  }

  if (entity.type === "mediaSeason") {
    return localizedPaths((lang) => resolveRoute(lang, "media", { slug: item.slug[lang] }));
  }

  const sectionByEntity = {
    program: "programs",
    episode: "episodes",
    course: "courses",
    article: "articles",
    download: "downloads"
  };

  return localizedPaths((lang) => resolveRoute(lang, sectionByEntity[entity.type], { slug: item.slug[lang] }));
}

export const seoSource = {
  articles,
  courses,
  downloads,
  episodes,
  events,
  library,
  mediaCycles,
  mediaSeasons: getAllMediaSeasons(),
  programs,
  seasons,
  text
};
